import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import HeaderTS from "../global/headerTS";
import Footer2 from "../global/footerv2";
import SectionTitle from "../section-title";
import Ctav3 from "../ctav3";
import acctData from "../../data/accounting-system.json";

// every screenshot is captured twice; the Chinese UI copy carries a -tc suffix
const localizedImage = (src, lng) =>
  lng && lng.startsWith("tc") ? src.replace(/\.webp$/, "-tc.webp") : src;

const ContactButtons = ({ hero, i18nPrefix }) => (
  <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-start gap-3">
    <a
      rel="noreferrer"
      href={`tel:${hero.contactPhone}`}
      className="btn__primary2 btn-md smooth"
    >
      <span>
        <i className="icofont-phone icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.phone`}>Phone</Trans>
      </span>
    </a>
    <a
      rel="noreferrer"
      href={`https://wa.me/${hero.contactWhatsApp.replace(/[\s+]/g, "")}`}
      className="btn__primary2 btn-md smooth"
      target="_blank"
    >
      <span>
        <i className="icofont-brand-whatsapp icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.whatsapp`}>WhatsApp Us</Trans>
      </span>
    </a>
    <a
      rel="noreferrer"
      href={`mailto:${hero.contactEmail}`}
      className="btn__primary2 btn-md smooth"
    >
      <span>
        <i className="icofont-email icofont-1x"></i>
        &nbsp;&nbsp;&nbsp;
        <Trans i18nKey={`${i18nPrefix}.email`}>eMail</Trans>
      </span>
    </a>
  </div>
);

// tabbed screenshot in a browser-style frame, used by the How It Works sections
const ShotTabs = ({ screens, i18nPrefix, lng }) => {
  const [activeId, setActiveId] = useState(screens[0].id);
  const shot = screens.find((s) => s.id === activeId) || screens[0];
  const src = localizedImage(shot.image, lng);
  return (
    <div className="tcm-shot acct-shot">
      <div className="erp-screens__tabs tcm-shot__tabs" role="tablist">
        {screens.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === shot.id}
            className={`erp-screens__tab ${s.id === shot.id ? "is-active" : ""}`}
            onClick={() => setActiveId(s.id)}
          >
            <i className={s.icon}></i>
            <Trans i18nKey={`${i18nPrefix}.screen${s.id}.title`}>{s.title}</Trans>
          </button>
        ))}
      </div>
      <div className="erp-screens__frame tcm-shot__frame" role="tabpanel">
        <div className="erp-screens__frame--bar">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <a href={src} target="_blank" rel="noreferrer">
          <img key={src} className="img-fluid" src={src} alt={shot.title} />
        </a>
      </div>
      <p className="tcm-shot__caption">
        <Trans i18nKey={`${i18nPrefix}.screen${shot.id}.description`}>
          {shot.description}
        </Trans>
      </p>
    </div>
  );
};

// icon + title + text rows, the about2 list from the Reboot template
const ItemList = ({ items, i18nPrefix }) => (
  <div className="about2__items tcm-items">
    {items.map((item) => (
      <div key={item.id} className="about2__items--single tcm-items__single">
        <div className="tcm-items__icon">
          <i className={item.icon}></i>
        </div>
        <div className="about2__items--text">
          <h3>
            <Trans i18nKey={`${i18nPrefix}.item${item.id}.title`}>{item.title}</Trans>
          </h3>
          <p>
            <Trans i18nKey={`${i18nPrefix}.item${item.id}.description`}>
              {item.description}
            </Trans>
          </p>
        </div>
      </div>
    ))}
  </div>
);

const AccountingSystem = ({ header }) => {
  const {
    acctHero,
    acctFeatures,
    acctErp,
    acctTax,
    acctInventory,
    acctPricing,
  } = acctData;
  const { i18n } = useTranslation();
  const lng = i18n.language;
  const integration = acctPricing.integration;

  return (
    <>
      <HeaderTS header={header} />

      {/* Hero Section */}
      <section id="hero" className="hero3 hero-padding bg-one tcm-hero acct-hero">
        <div className="container">
          <div className="row align-items-center gy-5">
            <div className="col-lg-6 hero3__content">
              <p className="badge-text">
                <Trans i18nKey="accountingSystem.hero.subtitle">
                  {acctHero.subtitle}
                </Trans>
              </p>
              <h1 className="display-5 mb-4 hero3__title">
                <Trans i18nKey="accountingSystem.hero.title">{acctHero.title}</Trans>
              </h1>
              <p className="fs-5 mb-4">
                <Trans i18nKey="accountingSystem.hero.description">
                  {acctHero.description}
                </Trans>
              </p>
              <p className="tcm-hero__price">
                <i className="icofont-tag"></i>
                <Trans i18nKey="accountingSystem.hero.price">{acctHero.price}</Trans>
              </p>
              <ContactButtons hero={acctHero} i18nPrefix="accountingSystem.hero" />
            </div>

            <div className="col-lg-6">
              <div className="tcm-hero__stack">
                <div className="tcm-hero__window tcm-hero__window--back">
                  <div className="erp-screens__frame--bar">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <img
                    src={localizedImage(acctHero.imageBack, lng)}
                    alt={acctHero.subtitle}
                  />
                </div>
                <div className="tcm-hero__window tcm-hero__window--front">
                  <div className="erp-screens__frame--bar">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <img
                    src={localizedImage(acctHero.imageFront, lng)}
                    alt={acctHero.subtitle}
                  />
                </div>
                <ul className="tcm-hero__chips">
                  {acctHero.chips.map((chip) => (
                    <li key={chip.id}>
                      <i className={chip.icon}></i>
                      <Trans i18nKey={`accountingSystem.hero.chip${chip.id}`}>
                        {chip.title}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="service section-padding acct-features">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="accountingSystem.features.subtitle">
                {acctFeatures.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="accountingSystem.features.title">
                {acctFeatures.title}
              </Trans>
            }
            direction="center"
          />
          <div className="row pt-4 g-4">
            {acctFeatures.features.map((feature) => (
              <div key={feature.id} className="col-md-6 col-xl-3">
                <div className="acct-feature translateEffect1 h-100">
                  <div className="acct-feature__head">
                    <span className="acct-feature__icon">
                      <i className={feature.icon}></i>
                    </span>
                    <h4>
                      <Trans
                        i18nKey={`accountingSystem.features.feature${feature.id}.title`}
                      >
                        {feature.title}
                      </Trans>
                    </h4>
                  </div>
                  <p>
                    <Trans
                      i18nKey={`accountingSystem.features.feature${feature.id}.description`}
                    >
                      {feature.description}
                    </Trans>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="acct-integrated">
            <p className="acct-integrated__title">
              <Trans i18nKey="accountingSystem.features.integratedTitle">
                {acctFeatures.integratedTitle}
              </Trans>
            </p>
            <ul>
              {acctFeatures.integrated.map((system) => (
                <li key={system.id}>
                  <i className={system.icon}></i>
                  <Trans i18nKey={`accountingSystem.features.integrated${system.id}`}>
                    {system.title}
                  </Trans>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How It Works 1: ERP Integrated */}
      <section id="erp" className="about2 section-padding bg-one tcm-how acct-erp">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="accountingSystem.erp.subtitle">{acctErp.subtitle}</Trans>
            }
            title={<Trans i18nKey="accountingSystem.erp.title">{acctErp.title}</Trans>}
            direction="center"
          />

          {/* every system feeds one posting engine, which feeds the books */}
          <div className="acct-engine">
            <div className="acct-engine__sources">
              {acctErp.sources.map((source) => (
                <div key={source.id} className="acct-node">
                  <span className="acct-node__icon">
                    <i className={source.icon}></i>
                  </span>
                  <div>
                    <h5>
                      <Trans i18nKey={`accountingSystem.erp.source${source.id}.title`}>
                        {source.title}
                      </Trans>
                    </h5>
                    <p>
                      <Trans
                        i18nKey={`accountingSystem.erp.source${source.id}.description`}
                      >
                        {source.description}
                      </Trans>
                    </p>
                  </div>
                </div>
              ))}
              <p className="acct-engine__label">
                <Trans i18nKey="accountingSystem.erp.journeyTitle">
                  {acctErp.journeyTitle}
                </Trans>
              </p>
              <ol className="acct-journey">
                {acctErp.journey.map((stop) => (
                  <li key={stop.id} className="acct-journey__stop">
                    <span className="acct-node__icon">
                      <i className={stop.icon}></i>
                    </span>
                    <h5>
                      <Trans i18nKey={`accountingSystem.erp.journey${stop.id}.title`}>
                        {stop.title}
                      </Trans>
                    </h5>
                    <p>
                      <Trans
                        i18nKey={`accountingSystem.erp.journey${stop.id}.description`}
                      >
                        {stop.description}
                      </Trans>
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            <div className="acct-engine__arrow" aria-hidden="true">
              <i className="icofont-arrow-right"></i>
            </div>

            <div className="acct-engine__core">
              <span className="acct-engine__core-icon">
                <i className="icofont-gears"></i>
              </span>
              <h4>
                <Trans i18nKey="accountingSystem.erp.engineTitle">
                  {acctErp.engineTitle}
                </Trans>
              </h4>
              <p>
                <Trans i18nKey="accountingSystem.erp.engine">{acctErp.engine}</Trans>
              </p>
            </div>

            <div className="acct-engine__arrow" aria-hidden="true">
              <i className="icofont-arrow-right"></i>
            </div>

            <ul className="acct-engine__outputs">
              {acctErp.outputs.map((output) => (
                <li key={output.id} className="acct-node acct-node--output">
                  <span className="acct-node__icon">
                    <i className={output.icon}></i>
                  </span>
                  <h5>
                    <Trans i18nKey={`accountingSystem.erp.output${output.id}`}>
                      {output.title}
                    </Trans>
                  </h5>
                </li>
              ))}
            </ul>
          </div>
          <p className="acct-engine__shared">
            <Trans i18nKey="accountingSystem.erp.shared">{acctErp.shared}</Trans>
          </p>

          <div className="row gx-xl-5 gy-5 align-items-center acct-erp__detail">
            <div className="col-lg-5">
              <h4 className="acct-subtitle">
                <Trans i18nKey="accountingSystem.erp.ledgerTitle">
                  {acctErp.ledgerTitle}
                </Trans>
              </h4>
              <ItemList items={acctErp.items} i18nPrefix="accountingSystem.erp" />
            </div>
            <div className="col-lg-7">
              <ShotTabs
                screens={acctErp.screens}
                i18nPrefix="accountingSystem.erp"
                lng={lng}
              />
            </div>
          </div>

          <div className="acct-postings">
            <h4 className="acct-subtitle text-center">
              <Trans i18nKey="accountingSystem.erp.postingsTitle">
                {acctErp.postingsTitle}
              </Trans>
            </h4>
            <div className="acct-table-wrap">
              <table className="acct-table">
                <thead>
                  <tr>
                    {["document", "debit", "credit"].map((col) => (
                      <th key={col}>
                        <Trans i18nKey={`accountingSystem.erp.columns.${col}`}>
                          {acctErp.columns[col]}
                        </Trans>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {acctErp.postings.map((row) => (
                    <tr key={row.id}>
                      {["document", "debit", "credit"].map((col) => (
                        <td key={col}>
                          <Trans i18nKey={`accountingSystem.erp.posting${row.id}.${col}`}>
                            {row[col]}
                          </Trans>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="erp-flow__note acct-note">
            <i className="icofont-check-circled"></i>
            <p>
              <Trans i18nKey="accountingSystem.erp.note">{acctErp.note}</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works 2: 3-Country Tax */}
      <section id="tax" className="about2 section-padding tcm-how acct-tax">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="accountingSystem.tax.subtitle">{acctTax.subtitle}</Trans>
            }
            title={<Trans i18nKey="accountingSystem.tax.title">{acctTax.title}</Trans>}
            direction="center"
          />

          <div className="acct-table-wrap acct-compare">
            <table className="acct-table">
              <thead>
                <tr>
                  <th aria-hidden="true"></th>
                  {acctTax.countries.map((country) => (
                    <th key={country.id}>
                      <Trans i18nKey={`accountingSystem.tax.country${country.id}`}>
                        {country.title}
                      </Trans>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {acctTax.rows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row">
                      <Trans i18nKey={`accountingSystem.tax.row${row.id}.title`}>
                        {row.title}
                      </Trans>
                    </th>
                    {row.cells.map((cell, j) => (
                      <td key={j}>
                        <Trans i18nKey={`accountingSystem.tax.row${row.id}.cell${j + 1}`}>
                          {cell}
                        </Trans>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row gx-xl-5 gy-5 align-items-center pt-5 mt-lg-3">
            <div className="col-lg-5">
              <ItemList items={acctTax.items} i18nPrefix="accountingSystem.tax" />
            </div>
            <div className="col-lg-7">
              <ShotTabs
                screens={acctTax.screens}
                i18nPrefix="accountingSystem.tax"
                lng={lng}
              />
            </div>
          </div>

          <div className="erp-flow__note acct-note">
            <i className="icofont-info-circle"></i>
            <p>
              <Trans i18nKey="accountingSystem.tax.note">{acctTax.note}</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works 3: Inventory Auditing */}
      <section
        id="inventory"
        className="about2 section-padding bg-one tcm-how acct-inventory"
      >
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="accountingSystem.inventory.subtitle">
                {acctInventory.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="accountingSystem.inventory.title">
                {acctInventory.title}
              </Trans>
            }
            direction="center"
          />
          <ol className="erp-flow__ribbon acct-ribbon">
            {acctInventory.steps.map((step, i) => (
              <li
                key={step.id}
                // earlier panels paint over the notch of the next one
                style={{ zIndex: acctInventory.steps.length - i }}
                className={`erp-flow__ribbon--step${
                  i === acctInventory.steps.length - 1 ? " is-final" : ""
                }`}
              >
                <i className={step.icon}></i>
                <h5>
                  <Trans i18nKey={`accountingSystem.inventory.step${step.id}.title`}>
                    {step.title}
                  </Trans>
                </h5>
                <span className="erp-flow__ribbon--doc">
                  <Trans
                    i18nKey={`accountingSystem.inventory.step${step.id}.description`}
                  >
                    {step.description}
                  </Trans>
                </span>
              </li>
            ))}
          </ol>

          <div className="row gx-xl-5 gy-5 align-items-center pt-5 mt-lg-3">
            <div className="col-lg-7 order-2 order-lg-1">
              <ShotTabs
                screens={acctInventory.screens}
                i18nPrefix="accountingSystem.inventory"
                lng={lng}
              />
            </div>
            <div className="col-lg-5 order-1 order-lg-2">
              <ItemList
                items={acctInventory.items}
                i18nPrefix="accountingSystem.inventory"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing section-padding acct-pricing">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="accountingSystem.pricing.subtitle">
                {acctPricing.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="accountingSystem.pricing.title">
                {acctPricing.title}
              </Trans>
            }
            direction="center"
          />

          <div className="row g-4 pt-4">
            {/* the two Basic plans, with what Basic includes underneath */}
            <div className="col-lg-8">
              <div className="row g-4">
                {acctPricing.plans.map((plan) => (
                  <div key={plan.id} className="col-md-6">
                    <div
                      className={`pricing__item acct-plan translateEffect1 h-100${
                        plan.highlight ? " acct-plan--highlight" : ""
                      }`}
                    >
                      {plan.badge && (
                        <span className="acct-plan__badge">
                          <Trans i18nKey={`accountingSystem.pricing.plan${plan.id}.badge`}>
                            {plan.badge}
                          </Trans>
                        </span>
                      )}
                      <p className="acct-plan__name">
                        <Trans i18nKey={`accountingSystem.pricing.plan${plan.id}.plan`}>
                          {plan.plan}
                        </Trans>
                      </p>
                      <h3 className="acct-plan__price">{plan.price}</h3>
                      <p className="acct-plan__per">
                        <Trans i18nKey={`accountingSystem.pricing.plan${plan.id}.per`}>
                          {plan.per}
                        </Trans>
                      </p>
                      <p className="acct-plan__note">
                        <Trans i18nKey={`accountingSystem.pricing.plan${plan.id}.note`}>
                          {plan.note}
                        </Trans>
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="acct-includes">
                <h4 className="acct-includes__title">
                  <Trans i18nKey="accountingSystem.pricing.includesTitle">
                    {acctPricing.includesTitle}
                  </Trans>
                </h4>
                <ul>
                  {acctPricing.includes.map((feature, j) => (
                    <li key={j}>
                      <i className="icofont-check"></i>
                      <Trans i18nKey={`accountingSystem.pricing.include${j + 1}`}>
                        {feature}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="pricing__item acct-plan acct-plan--integration translateEffect1 h-100">
                <p className="acct-plan__name">
                  <Trans i18nKey="accountingSystem.pricing.integration.plan">
                    {integration.plan}
                  </Trans>
                </p>
                <h3 className="acct-plan__price acct-plan__price--text">
                  <Trans i18nKey="accountingSystem.pricing.integration.price">
                    {integration.price}
                  </Trans>
                </h3>
                <p className="acct-plan__note">
                  <Trans i18nKey="accountingSystem.pricing.integration.note">
                    {integration.note}
                  </Trans>
                </p>
                <ul className="acct-plan__systems">
                  {integration.items.map((item) => (
                    <li key={item.id}>
                      <span>
                        <i className={item.icon}></i>
                      </span>
                      <Trans i18nKey={`accountingSystem.pricing.integration.item${item.id}`}>
                        {item.title}
                      </Trans>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="acct-pricing__contact">
            <ContactButtons hero={acctHero} i18nPrefix="accountingSystem.pricing" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <Ctav3 className="acct-cta" />

      <Footer2 />
    </>
  );
};

export default AccountingSystem;
